/**
 * KPI percentage rounding (CR-6155).
 *
 * OTIF and OTD arrive from the dashboards' API unrounded so they are rounded
 * exactly once, to the company's KPI decimal setting
 * (`preferences.kpi_percent_decimals`, Admin Setup -> Regional). Every ring,
 * chart tooltip, drill-down header and CSV export goes through these helpers,
 * so they can never disagree with one another.
 */

/** Whole, non-negative decimal places; anything unusable counts as 0. */
function toPlaces(decimals: number): number {
  return Number.isFinite(decimals) ? Math.max(0, Math.trunc(decimals)) : 0
}

/**
 * Round half away from zero to `decimals` places, on the number as written.
 *
 * `toFixed` rounds the binary float, so (2.675).toFixed(2) is "2.67" and
 * (91.955).toFixed(2) is "91.95". Shifting the decimal point in the number's
 * text form first rounds the figure a person reads.
 *
 * @param value - the number to round
 * @param decimals - decimal places, 0 or more
 * @returns the rounded number
 */
export function roundHalfUp(value: number, decimals: number): number {
  const places = toPlaces(decimals)
  const abs = Math.abs(value)
  const text = String(abs)
  // Exponent forms (1e-7, 1e+21) cannot take a second exponent.
  const rounded = text.includes('e')
    ? Math.round(abs * 10 ** places) / 10 ** places
    : Number(`${Math.round(Number(`${text}e${places}`))}e-${places}`)
  return value < 0 ? -rounded : rounded
}

/**
 * Format a KPI percentage (OTIF, OTD) to the company's KPI decimal setting.
 *
 * The one rule every ring, tooltip, drill-down header and export uses, so they
 * can never disagree. Pass the unrounded value — the dashboards' API sends it
 * that way so the number is rounded only once (CR-6155).
 *
 * @param value - the unrounded percentage; null/undefined/NaN when there is no data
 * @param decimals - `preferences.kpi_percent_decimals` (0–2)
 * @param empty - text for a missing value
 * @returns e.g. "24.51%", or `empty`
 * @example formatKpiPercent(24.5088, 0) // "25%"
 */
export function formatKpiPercent(
  value: number | null | undefined,
  decimals: number,
  empty = 'N/A',
): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return empty
  const places = toPlaces(decimals)
  return `${roundHalfUp(value, places).toFixed(places)}%`
}

/** The OTIF/OTD percentage columns the dashboard charts carry (`target` is the % target line). */
const KPI_PERCENT_COLUMNS = ['otif', 'otd', 'otifTrend', 'otdTrend', 'target']

/**
 * Round the OTIF/OTD percentage columns of chart rows for a CSV export.
 *
 * Other columns (counts, labels) are left as they are. A missing value stays
 * missing.
 *
 * @param rows - the chart's data rows
 * @param decimals - `preferences.kpi_percent_decimals` (0–2)
 * @returns new rows; percentages become strings such as "21.95"
 */
export function roundKpiPercentColumns<T extends Record<string, unknown>>(
  rows: T[],
  decimals: number,
): T[] {
  const places = toPlaces(decimals)
  return rows.map((row) => {
    const out: Record<string, unknown> = { ...row }
    for (const key of KPI_PERCENT_COLUMNS) {
      const value = out[key]
      if (typeof value === 'number' && Number.isFinite(value)) {
        out[key] = roundHalfUp(value, places).toFixed(places)
      }
    }
    return out as T
  })
}
